import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Search, Loader2, AlertCircle, Paperclip, FolderOpen, RefreshCw, ExternalLink } from 'lucide-react';
import { formatDateTime, formatRelative } from '../../utils/dateUtils';
import EmailViewModal from '../EmailIntelligence/EmailViewModal';
import { getCaseClassifiedEmails } from '../../services/emailService';

const CaseEmails = ({ caseId }) => {
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [viewEmailId, setViewEmailId] = useState(null);

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (searchDebounce) params.search = searchDebounce;
      const data = await getCaseClassifiedEmails(caseId, params);
      setEmails(data.emails || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load case emails');
    } finally {
      setLoading(false);
    }
  }, [caseId, searchDebounce]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const urgencyColors = {
    critical: 'bg-red-500/20 text-red-300',
    high: 'bg-orange-500/20 text-orange-300',
    normal: 'bg-blue-500/20 text-blue-300',
    low: 'bg-slate-500/20 text-slate-300'
  };

  const filingSourceLabels = {
    auto: { text: 'Auto-filed', color: 'bg-green-500/20 text-green-300' },
    suggested: { text: 'AI Suggested', color: 'bg-cyan-500/20 text-cyan-300' },
    manual: { text: 'Manual', color: 'bg-slate-500/20 text-slate-300' },
    corrected: { text: 'Corrected', color: 'bg-amber-500/20 text-amber-300' }
  };

  const typeIcons = {
    court_filing: '⚖️', client_inquiry: '👤', opposing_counsel: '⚔️', vendor: '📦', other: '📧'
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Case Emails</h3>
          <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full text-sm">{total}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search emails..."
              className="pl-9 pr-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-sm text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 w-64" />
          </div>
          <button onClick={fetchEmails}
            className="p-2 bg-virgo-navy-600 hover:bg-virgo-navy-500 border border-virgo-teal-700/50 rounded-lg transition-colors">
            <RefreshCw className="w-4 h-4 text-slate-300" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      ) : emails.length === 0 ? (
        <div className="text-center py-12">
          <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 font-medium">No emails linked to this case</p>
          <p className="text-sm text-slate-500 mt-1">Emails will appear here when filed to this case manually or by Astraea</p>
        </div>
      ) : (
        <div className="space-y-2">
          {emails.map((email) => {
            const source = filingSourceLabels[email.filing_source] || filingSourceLabels.manual;
            return (
              <div key={email.email_id} onClick={() => setViewEmailId(email.email_id)}
                className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-lg p-4 cursor-pointer hover:border-cyan-500/30 hover:bg-virgo-navy-700/50 transition-all group">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!email.is_read && <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0" />}
                      <span className="text-sm font-medium text-white truncate">{email.subject || '(No subject)'}</span>
                      {email.has_attachments && <Paperclip className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                    </div>
                    <div className="text-xs text-slate-400">
                      {email.from} &middot; {email.received_at ? formatRelative(email.received_at) : ''}
                    </div>
                    {email.ai_summary && <p className="text-xs text-slate-500 mt-1 truncate">{email.ai_summary}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {email.email_type && <span className="text-xs">{typeIcons[email.email_type] || '📧'}</span>}
                    {email.urgency && email.urgency !== 'normal' && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${urgencyColors[email.urgency] || urgencyColors.normal}`}>
                        {email.urgency}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${source.color}`}>{source.text}</span>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewEmailId && <EmailViewModal emailId={viewEmailId} onClose={() => setViewEmailId(null)} />}
    </div>
  );
};

export default CaseEmails;
